import Vue from 'vue'
import {
  SET_ORDER_VALIDATION_ERRORS,
  ADD_ORDER_ITEM,
  REMOVE_ITEM_FROM_ORDER_BY_ID,
  SET_DISCOUNT_CODE,
  SET_PRICE_SPECIFICATION,
  SET_PAYMENT_SPECIFICATION,
  SET_TERMS_FOR_CART_ITEM,
  CLEAR_ORDER_VALIDATION_ERRORS,
  CLEAR_ORDER_CREATED,
  CLEAR_PAY_IN_TERMS,
  SET_TERM_AGREEMENT,
  SET_PAY_IN_TERMS,
  SET_COMBI_DEAL_DISCOUNT_SUGGESTIONS,
  CLEAR_TERMS_FOR_ALL_CART_ITEMS,
  CUSTOMER_DETAILS_CHANGED,
  SET_SHOP,
  CLEAR_SHOP
} from './mutation-types'
import ValidationError from '../../../order-api/error/validation-error'
import {PaymentTermConfiguration} from '../../../types/payment-term-configuration'
import type {CartItem} from '../../../types/cart-item'
import OrderItemPaymentConfiguration from '../../../types/order-item-payment-configuration'
import type {Order} from '../../../types/order'
import type {PropertyError} from '../../../types/validation-result'
import type {OrderValidationStage} from '../../../order-api/order-api-client-interface'

async function validateOrder (order: Order, stage: OrderValidationStage = 'cart'): Promise<Array<PropertyError>> {
  try {
    const orderValidationResult = await Vue.$container.orderApiClient.validateOrder(order, stage)
    return orderValidationResult.hasOwnProperty('errors') ? orderValidationResult.errors : []
  } catch (error) {
    if (error.errors) {
      return error.errors
    }

    throw error
  }
}

export default {
  addOrderItem: async ({ state, commit, dispatch }, indexedOrderItemObj) => {
    if (Object.entries(state.order.items).length === 0) {
      await dispatch('setShop')
    }
    commit(ADD_ORDER_ITEM, indexedOrderItemObj)
    await dispatch('clearOrderCreated')
  },
  removeItemFromOrderById: async ({ state, commit, dispatch }, orderItemId) => {
    commit(REMOVE_ITEM_FROM_ORDER_BY_ID, orderItemId)
    await dispatch('clearOrderCreated')
    await dispatch('updateCombiDealDiscountSuggestions')
    await dispatch('addDiscountCode', state.discountCode || null) // also updates the price & payment specs
    await dispatch('clearPayInTerms')

    if (Object.entries(state.order.items).length === 0) {
      await dispatch('clearShop')
    }
  },
  updatePriceSpecification: async ({ state, commit }) => {
    const specification = await Vue.prototype.$container.orderApiClient.getPriceSpecificationForOrder(state.order)
    commit(SET_PRICE_SPECIFICATION, specification)
  },
  updateCombiDealDiscountSuggestions: async ({ state, commit }) => {
    const suggestions = await Vue.prototype.$container.orderApiClient.getCombiDealDiscountSuggestionsForOrder(state.order)
    commit(SET_COMBI_DEAL_DISCOUNT_SUGGESTIONS, suggestions.combiDealDiscountSuggestions)
  },
  updatePaymentSpecification: async ({ rootState, commit }) => {
    const specification = await Vue.prototype.$container.orderApiClient.getPaymentSpecificationForOrder(rootState.cart.order)
    commit(SET_PAYMENT_SPECIFICATION, specification)
  },
  clearPayInTerms: async ({ commit, dispatch }) => {
    commit(CLEAR_PAY_IN_TERMS)
    commit(SET_TERM_AGREEMENT, false)
    await dispatch('resetTermsForAllCartProductItems')
  },
  setTermsForCartProductItem: ({ commit }, { terms, cartProductItem }) => {
    commit(SET_TERMS_FOR_CART_ITEM, { terms, cartProductItem })
  },
  // @deprecated; use resetTermsForAllCartProductItems
  clearTermsForAllCartProductItems: ({commit}) => {
    commit(CLEAR_TERMS_FOR_ALL_CART_ITEMS)
  },
  resetTermsForAllCartProductItems: ({commit, rootGetters}) => {
    const paymentConfigurationOptions: Array<OrderItemPaymentConfiguration> = rootGetters['payment/paymentConfigurations']
    rootGetters['cart/cartProductItems'].forEach(
      (cartProductItem: CartItem) => {
        if (cartProductItem.orderItemId in paymentConfigurationOptions) {
          const itemPaymentTermConfigurations = paymentConfigurationOptions[cartProductItem.orderItemId].paymentTermConfigurations
          const configurationsByType = groupBy(itemPaymentTermConfigurations, (configuration: PaymentTermConfiguration) => configuration.type)
          const preferPayInTerms = rootGetters['cart/payInTerms'] || false
          const preferredType = preferPayInTerms ? 'TERM' : 'DIRECT'

          const actualType = preferredType in configurationsByType ? preferredType : Object.keys(configurationsByType)[0]
          const chosenConfiguration = actualType in configurationsByType ? configurationsByType[actualType][0] : null

          if (chosenConfiguration) {
            commit(SET_TERMS_FOR_CART_ITEM, {terms: chosenConfiguration.nrOfTerms, cartProductItem})
          }
        }
      }
    )
  },
  addDiscountCode: async ({ state, commit, dispatch }, discountCode) => {
    const orderWithDiscount = { ...state.order, discountCode }

    try {
      const validationErrors = await validateOrder(orderWithDiscount)
      if (validationErrors.length === 0) {
        await dispatch('updateOrder', {order: orderWithDiscount})
        commit(SET_DISCOUNT_CODE, discountCode)
      } else {
        throw new ValidationError(validationErrors, new Error(validationErrors[0].message))
      }
    } catch (error) {
      commit(SET_DISCOUNT_CODE, null)
      throw error
    } finally {
      dispatch('updatePriceSpecification')
      dispatch('updatePaymentSpecification')
    }
  },
  validateOrder: async ({ state, commit }, { order, stage }) => {
    const propertyErrors = await validateOrder(order, stage)
    commit(SET_ORDER_VALIDATION_ERRORS, propertyErrors)

    return propertyErrors
  },
  assertOrderValid: async ({ state, commit, dispatch }, { order, stage }) => {
    const propertyErrors = await dispatch('validateOrder', { order, stage })
    if (propertyErrors.length > 0) {
      throw new ValidationError(propertyErrors, new Error('Order is not valid'))
    }
  },
  updateOrder: async ({ state, commit, dispatch }, { order }) => {
    try {
      const orderUpdateResult = await Vue.$container.orderApiClient.updateOrder(order)
      const errors = orderUpdateResult.hasOwnProperty('errors') ? orderUpdateResult.errors : []
      if (errors.length) {
        commit(SET_ORDER_VALIDATION_ERRORS, errors)
        throw new ValidationError(errors, new Error('Order update failed'))
      } else {
        commit(CLEAR_ORDER_VALIDATION_ERRORS)
      }
      return orderUpdateResult
    } catch (error) {
      if (error.errors) {
        commit(SET_ORDER_VALIDATION_ERRORS, error.errors)
      }
      throw error
    }
  },
  clearOrderCreated: async ({ commit }) => {
    commit(CLEAR_ORDER_CREATED)
  },
  switchPayInTerms: async ({ commit, dispatch }, payInTerms: boolean) => {
    commit(SET_PAY_IN_TERMS, payInTerms)
    if (!payInTerms) {
      await dispatch('switchTermAgreement', false)
    }
    await dispatch('resetTermsForAllCartProductItems')
  },
  switchTermAgreement: async ({commit}, agreeToTermPayments: boolean) => {
    commit(SET_TERM_AGREEMENT, agreeToTermPayments)
  },
  resetCustomerDetailsChanged: async ({ commit }) => {
    commit(CUSTOMER_DETAILS_CHANGED, false)
  },
  setShop: async ({rootState, commit}) => {
    commit(SET_SHOP, rootState.shop.shopConfig)
  },
  clearShop: async ({commit}) => {
    commit(CLEAR_SHOP)
  }
}

// turn an array into a set of arrays grouped by a string key
function groupBy<T> (array: Array<T>, keyFunction: (T => string)): {[string]: Array<T>} {
  return array.reduce(
    (groupedElements, item) => {
      const key = keyFunction(item)
      groupedElements[key] = [...(key in groupedElements ? groupedElements[key] : []), item]

      return groupedElements
    },
    {}
  )
}



// WEBPACK FOOTER //
// ./src/store/modules/cart/actions.js